import type { FC, ReactNode } from 'react'
import { createContext, useState, useContext, useMemo } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import { leasingAssetActions } from '@/modules/admin/resources/assets/constants'
import type {
  EndOfLeaseAssetsEmployeeSelectionValidate,
  EndOfLeaseAssetsValidate,
  ExtendAssetsValidate,
  ReturnAssetsValidate,
} from '@/resources/leasing'
import { leasingResources } from '@/resources/leasing'

import type { Asset } from '../resources'

type AssetsLeasedContextType = {
  selectedList: number[]
  unavailableAssets:
    | EndOfLeaseAssetsValidate
    | ExtendAssetsValidate
    | ReturnAssetsValidate
    | EndOfLeaseAssetsEmployeeSelectionValidate
  action: string | number | null
  isActionPopupOpen: boolean

  setSelectedList: (list: number[]) => void
  setUnavailableAssets: (
    list:
      | EndOfLeaseAssetsValidate
      | ExtendAssetsValidate
      | ReturnAssetsValidate
      | EndOfLeaseAssetsEmployeeSelectionValidate
  ) => void
  setAction: (name: string | number | null) => void
  setIsActionPopupOpen: (open: boolean) => void

  clearSelectedList: () => void
  toggleSelect: (id: number) => void
  selectAll: (visibleListAssets: Asset[]) => void
  handleActionChange: (value: string | number) => void
  onConfirmClick: () => void
  resetMutations: () => void

  isLoadingValidation: boolean
  isErrorValidation: boolean
  isSuccessValidation: boolean
  isLoadingConfirm: boolean
  isErrorConfirm: boolean
  isSuccessConfirm: boolean
}

const AssetsLeasedContext = createContext<AssetsLeasedContextType | undefined>(undefined)

export const useLeasedAssets = () => {
  const context = useContext(AssetsLeasedContext)
  if (!context) {
    throw new Error('useLeased must be used within an AssetsLeasedProvider')
  }
  return context
}

export const AssetsLeasedProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [queryAssetOwnership] = useSearchParamsByKey('assetOwnership')
  const [queryActiveEndOfLease] = useSearchParamsByKey('activeEndOfLease')
  const [queryIsNearLeaseEnd] = useSearchParamsByKey('isNearLeaseEnd')
  const [queryNotConnected] = useSearchParamsByKey('notConnected')

  const [selectedList, setSelectedList] = useState<number[]>([])
  const [unavailableAssets, setUnavailableAssets] = useState<
    | EndOfLeaseAssetsValidate
    | ExtendAssetsValidate
    | ReturnAssetsValidate
    | EndOfLeaseAssetsEmployeeSelectionValidate
  >([])
  const [action, setAction] = useState<string | number | null>(null)
  const [isActionPopupOpen, setIsActionPopupOpen] = useState(false)
  const queryClient = useQueryClient()

  const isActionExtend = Number.isFinite(action)
  const isEndOfLeaseAction = action === leasingAssetActions.LEASE_END
  const isEmployeeSelectionAction = action === leasingAssetActions.EMPLOYEE_SELECTION

  const clearSelectedList = () => setSelectedList([])
  const toggleSelect = (id: number) => {
    const list = selectedList.includes(id)
      ? selectedList.filter(el => el !== id)
      : [...selectedList, id]
    setSelectedList(list)
  }
  const selectAll = (assetsList: Asset[]) => {
    const list = assetsList.length === selectedList.length ? [] : assetsList.map(el => el.id)
    setSelectedList(list)
  }

  const triggerInvalidation = (assetIds: number[]) => {
    setTimeout(() => {
      queryClient.invalidateQueries(
        queries.adminAssetList({
          allAssets: true,
          assetOwnership: (queryAssetOwnership as 'LEASED' | 'OWNED') || undefined,
          activeEndOfLease: queryActiveEndOfLease === 'true',
          isNearLeaseEnd: queryIsNearLeaseEnd === 'true',
          notConnected: queryNotConnected === 'true',
        }),
        {
          refetchType: 'all',
        }
      )

      assetIds.forEach((assetId: number) => {
        queryClient.invalidateQueries(queries.adminAsset(assetId), { refetchType: 'all' })
      })
    }, 3000)
  }

  const {
    mutate: validateEOLAssetMutation,
    isLoading: isLoadingValidateEOL,
    isError: isErrorValidateEOL,
    isSuccess: isSuccessValidateEOL,
    reset: resetValidateEOLAsset,
  } = useMutation(leasingResources.endOfLeaseAssetsValidate, {
    onSuccess(data) {
      setUnavailableAssets(data)
    },
  })
  const {
    isLoading: isLoadingEOL,
    isError: isErrorEOL,
    isSuccess: isSuccessEOL,
    mutate: eolAssetMutation,
    reset: resetEOLAsset,
  } = useMutation(leasingResources.endOfLeaseAssets, {
    onSuccess() {
      setUnavailableAssets([])
    },
    onSettled(data, err, variables: { assetIds: number[] }) {
      triggerInvalidation(variables.assetIds)
    },
  })

  const {
    mutate: validateEOLEmployeeSelectionAssetMutation,
    isLoading: isLoadingValidateEOLEmployeeSelection,
    isError: isErrorValidateEOLEmployeeSelection,
    isSuccess: isSuccessValidateEOLEmployeeSelection,
    reset: resetValidateEOLAssetEmployeeSelection,
  } = useMutation(leasingResources.endOfLeaseAssetsEmployeeSelectionValidate, {
    onSuccess(data) {
      setUnavailableAssets(data)
    },
  })
  const {
    isLoading: isLoadingEOLEmployeeSelection,
    isError: isErrorEOLEmployeeSelection,
    isSuccess: isSuccessEOLEmployeeSelection,
    mutate: eolEmployeeSelectionAssetMutation,
    reset: resetEOLEmployeeSelectionAsset,
  } = useMutation(leasingResources.endOfLeaseAssetsEmployeeSelection, {
    onSuccess() {
      setUnavailableAssets([])
    },
    onSettled(data, err, variables: { assetIds: number[] }) {
      triggerInvalidation(variables.assetIds)
    },
  })

  const {
    mutate: validateReturnAssetMutation,
    isLoading: isLoadingValidateReturn,
    isError: isErrorValidateReturn,
    isSuccess: isSuccessValidateReturn,
    reset: resetValidateReturnAsset,
  } = useMutation(leasingResources.returnAssetsValidate, {
    onSuccess(data) {
      setUnavailableAssets(data)
    },
  })
  const {
    isLoading: isLoadingReturn,
    isError: isErrorReturn,
    isSuccess: isSuccessReturn,
    mutate: returnAssetMutation,
    reset: resetReturnAsset,
  } = useMutation(leasingResources.returnAssets, {
    onSuccess() {
      setUnavailableAssets([])
    },
    onSettled(data, err, variables: { assetIds: number[] }) {
      triggerInvalidation(variables.assetIds)
    },
  })

  const {
    mutate: validateExtendAssetMutation,
    isLoading: isLoadingValidateExtend,
    isError: isErrorValidateExtend,
    isSuccess: isSuccessValidateExtend,
    reset: resetValidateExtendAsset,
  } = useMutation(leasingResources.extendAssetsValidate, {
    onSuccess(data) {
      setUnavailableAssets(data)
    },
  })
  const {
    isLoading: isLoadingExtend,
    isError: isErrorExtend,
    isSuccess: isSuccessExtend,
    mutate: extendAssetMutation,
    reset: resetExtendAsset,
  } = useMutation(leasingResources.extendAssets, {
    onSuccess() {
      setUnavailableAssets([])
    },
    onSettled(data, err, variables: { assetIds: number[] }) {
      triggerInvalidation(variables.assetIds)
    },
  })

  const handleActionChange = (value: string | number) => {
    setAction(value)
    setIsActionPopupOpen(true)
    if (value === leasingAssetActions.LEASE_END) {
      const payload = { assetIds: selectedList }
      validateEOLAssetMutation(payload)
    } else if (value === leasingAssetActions.EMPLOYEE_SELECTION) {
      const payload = { assetIds: selectedList }
      validateEOLEmployeeSelectionAssetMutation(payload)
    } else if (value === leasingAssetActions.RETURN) {
      const payload = { assetIds: selectedList }
      validateReturnAssetMutation(payload)
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      const payload = { assetIds: selectedList, numberOfMonths: value }
      validateExtendAssetMutation(payload)
    }
  }

  const onConfirmClick = () => {
    if (action === leasingAssetActions.LEASE_END) {
      const payload = { assetIds: selectedList }
      return eolAssetMutation(payload)
    } else if (action === leasingAssetActions.RETURN) {
      const payload = { assetIds: selectedList }
      returnAssetMutation(payload)
    } else if (action === leasingAssetActions.EMPLOYEE_SELECTION) {
      const payload = { assetIds: selectedList }
      return eolEmployeeSelectionAssetMutation(payload)
    } else if (typeof action === 'number' && Number.isFinite(action)) {
      const payload = { assetIds: selectedList, numberOfMonths: action }
      extendAssetMutation(payload)
    }
  }

  const getValidationStatus = () => {
    if (isActionExtend) {
      return {
        isLoadingValidation: isLoadingValidateExtend,
        isErrorValidation: isErrorValidateExtend,
        isSuccessValidation: isSuccessValidateExtend,
      }
    }
    if (isEndOfLeaseAction) {
      return {
        isLoadingValidation: isLoadingValidateEOL,
        isErrorValidation: isErrorValidateEOL,
        isSuccessValidation: isSuccessValidateEOL,
      }
    }

    if (isEmployeeSelectionAction) {
      return {
        isLoadingValidation: isLoadingValidateEOLEmployeeSelection,
        isErrorValidation: isErrorValidateEOLEmployeeSelection,
        isSuccessValidation: isSuccessValidateEOLEmployeeSelection,
      }
    }
    // RETURN action
    return {
      isLoadingValidation: isLoadingValidateReturn,
      isErrorValidation: isErrorValidateReturn,
      isSuccessValidation: isSuccessValidateReturn,
    }
  }

  const { isLoadingValidation, isErrorValidation, isSuccessValidation } = getValidationStatus()

  const getConfirmStatus = () => {
    if (isActionExtend) {
      return {
        isLoadingConfirm: isLoadingExtend,
        isErrorConfirm: isErrorExtend,
        isSuccessConfirm: isSuccessExtend,
      }
    }
    if (isEndOfLeaseAction) {
      return {
        isLoadingConfirm: isLoadingEOL,
        isErrorConfirm: isErrorEOL,
        isSuccessConfirm: isSuccessEOL,
      }
    }
    if (isEmployeeSelectionAction) {
      return {
        isLoadingConfirm: isLoadingEOLEmployeeSelection,
        isErrorConfirm: isErrorEOLEmployeeSelection,
        isSuccessConfirm: isSuccessEOLEmployeeSelection,
      }
    }

    // RETURN action
    return {
      isLoadingConfirm: isLoadingReturn,
      isErrorConfirm: isErrorReturn,
      isSuccessConfirm: isSuccessReturn,
    }
  }
  const { isLoadingConfirm, isErrorConfirm, isSuccessConfirm } = getConfirmStatus()

  const resetMutations = () => {
    if (isLoadingValidation || isErrorValidation || isSuccessValidation) {
      resetValidateEOLAsset()
      resetValidateEOLAssetEmployeeSelection()
      resetValidateExtendAsset()
      resetValidateReturnAsset()
    }
    if (isLoadingConfirm || isErrorConfirm || isSuccessConfirm) {
      resetEOLAsset()
      resetEOLEmployeeSelectionAsset()
      resetExtendAsset()
      resetReturnAsset()
    }
  }

  const value: AssetsLeasedContextType = useMemo(
    () => ({
      selectedList,
      unavailableAssets,
      action,
      isActionPopupOpen,
      isLoadingValidation,
      isErrorValidation,
      isSuccessValidation,
      isLoadingConfirm,
      isErrorConfirm,
      isSuccessConfirm,
      setSelectedList,
      setUnavailableAssets,
      setAction,
      setIsActionPopupOpen,
      clearSelectedList,
      toggleSelect,
      selectAll,
      handleActionChange,
      onConfirmClick,
      resetMutations,
    }),
    [
      action,
      isActionPopupOpen,
      selectedList,
      unavailableAssets,
      isLoadingValidation,
      isErrorValidation,
      isSuccessValidation,
      isLoadingConfirm,
      isErrorConfirm,
      isSuccessConfirm,
    ]
  )
  return <AssetsLeasedContext.Provider value={value}>{children}</AssetsLeasedContext.Provider>
}
